import { IPlanMeta } from '../types/plans';
import { IUser, UserRole } from '../types/user';
import { ImageUploadResponse } from '../types/files';
import axios from 'axios';
import ReactGA from 'react-ga4';
import * as uuid from 'uuid';

export const gaEvent = (
  category: string,
  action: string,
  label: string,
  value = 0,
  nonInteraction = true,
  transport = 'xhr' as const
) => {
  ReactGA.event({
    category,
    action,
    label, // optional
    value, // optional, must be a number
    nonInteraction, // optional, true/false
    transport, // optional, beacon/xhr/image
  });
};

export const formatPrice = (price: number) => new Intl.NumberFormat('en-US').format(price);

export const canBeShowed = (user: IUser, roles: string[] = []) => {
  if (!roles.length) return true;
  if (!user || !Array.isArray(user.roles) || !user.roles.length) return null;
  return roles.some(role => user.roles.includes(role as UserRole));
};

export const pluralize = (word: string): string => {
  if (word.endsWith('y')) {
    return word.slice(0, -1) + 'ies';
  }
  return word + 's';
};

export const uploadImage = (
  file: File,
  folder: string,
  addWatermark = false
): Promise<ImageUploadResponse> => {
  return new Promise((resolve, reject) => {
    const fd = new FormData();

    fd.append('name', file.name);
    fd.append('image', file);
    fd.append('folder', folder);
    fd.append('addWatermark', addWatermark.toString());

    axios({
      method: 'post',
      url: '/api/upload/image',
      data: fd,
    })
      .then(result => {
        if (result.status === 200) {
          const { data } = result;
          if (data.status === 'success') resolve(data.data);
          if (data.status === 'error') reject(data.message);
        } else {
          reject(result.status);
        }
      })
      .catch(reject);
  });
};

export function base64ToFile(base64String: string, filename: string): File {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  const blob = new Blob([u8arr], { type: mime });
  return new File([blob], filename, { type: mime });
}

export const getTitle = (meta: IPlanMeta) => {
  if (!meta) return 'House';

  let title = '';
  const { bathroomCount, bedroomCount } = meta;

  if (bedroomCount === 0) title += 'Studio ';
  if (bedroomCount && bedroomCount > 0) title += `${bedroomCount} BD`;

  if (bedroomCount && bedroomCount > 0 && bathroomCount) title += ` / ${bathroomCount} BATH`;

  return title;
};

export const getAreaTitle = (meta: IPlanMeta) => {
  if (!meta || !meta.totalArea) return '';

  return `${meta.outerArea || meta.totalArea} SQ FT`;
};

export const getFileExtension = (contentType: string) => {
  const extensions = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
  };
  return extensions[contentType as keyof typeof extensions] || 'jpg';
};

export const generateId = () => uuid.v4();
